import {useDispatch, useSelector} from "react-redux";
import useBusinessLocations from "../../../services/state/account/useBusinessLocations";
import {useState} from "react";
import CreditCard from "../../../logic/payment/creditCard";
import PaymentProcessor, {runInitialPaymentStep} from "../../../../components/payment/PaymentProcessor";
import useValueChangeListener from "../../../services/state/general/useValueChangeListener";
import Input from "../../../../components/general/input/Input";
import {AddressInput} from "../../../../components/checkout/form/InputDelivery";
import PaymentMethodSwitcher from "../../payment/PaymentMethodSwitcher";
import {PaymentRules} from "../../../logic/payment/paymentRules";
import {Button} from "../../../../components/general/input/Button";
import Popup from "../../../../components/general/popup/Popup";
import DialogOld from "../../general/dialog/DialogOld";
import useBanking from "../../../services/state/account/useBanking";
import LoadingSwitcher from "../../general/loading/LoadingSwitcher";
import {cardInfo} from "../../../../res/dataServices/cardInfo";

const formatDetails = (session, cardInput, amount, location, surcharge) => {

    const paymentDetails = cardInput?.customerPaymentProfileID ? cardInput : cardInput.cardInput;

    return {
        ...paymentDetails,
        currency: 'GBP',
        amount: (Number(amount) + Number(surcharge)).toFixed(2),
        surcharge,
        locationID: location.id,
        bPartnerID: session.bPartnerID,
        userID: session.userID,
    }
}

const splitName = (value) => {
    const namesArray = value.trim().split(" ");
    const lastName = namesArray.length > 1 ? namesArray.pop() : '';
    const firstName = namesArray.join(" ");
    return [firstName, lastName];
}

const MakePayment = ({ afterPayment }) => {

    const dispatch = useDispatch();

    const { session } = useSelector(state => state.local.auth);
    const { locations } = useBusinessLocations();
    const { paymentResult, error: paymentError } = useSelector(
        state => state.session.payment
    );
    const [ warning, setWarning ] = useState();

    const [ cardInput, setCardInput ] = useState();
    const [ billingAddress, setBillingAddress ] = useState();
    const [ paymentAmount, setPaymentAmount ] = useState();
    const [ showPaymentMade, setShowPaymentMade ] = useState(false);

    const readyToMakePayment =
        (CreditCard.validateCardInput(cardInput).ok
            || cardInput?.customerPaymentProfileID
        )
        && billingAddress
        && paymentAmount
        && paymentAmount > 0;

    const { banking, loading, getSavedCards } = useBanking();
    const savedCards =  getSavedCards(banking);
    const chargeRate = cardInput?.CreditCardSurchargeRate;
    const hasChargeRate = Boolean(chargeRate && chargeRate > 0);
    const surcharge = hasChargeRate ? (paymentAmount * (chargeRate/100)).toFixed(2) : 0;

    const onPayClick = () => {
        if(readyToMakePayment) {
            const details = formatDetails(session, cardInput, paymentAmount, billingAddress, surcharge);
            runInitialPaymentStep(dispatch, session, details);
        }
    }

    const onPaymentMade = () => {
        if (paymentResult?.success) {
            setShowPaymentMade(true);
            if(afterPayment) afterPayment();
        } else if(paymentError || paymentResult?.declined) {
            setWarning("Failed to take payment");
        }
    }

    const onSavedCardSelected = (bank) => {
        const [firstName, lastName] = splitName(bank.A_Name);
        const savedCard = {
            bankID: bank.id,
            customerPaymentProfileID: bank.CustomerPaymentProfileID,
            expireMonth: bank.CreditCardExpMM,
            expireYear: bank.CreditCardExpYY,
            cardType: cardInfo.getCodeForIdempiereID(bank.CreditCardType.id),
            firstName,
            lastName,
            email: bank.A_Email,
            CreditCardSurchargeRate: bank.CreditCardSurchargeRate,
        }
        setCardInput(savedCard);
    }

    useValueChangeListener(onPaymentMade, [ paymentResult?.success, paymentError ]);

    return (
        <>
            {warning &&
                <Popup>
                    <DialogOld
                        title="Payment Failed"
                        message="Payment could not be taken. Please check your details and try again."
                        neutralButton={{name: "OK", onClick: () => setWarning(null)}}
                    />
                </Popup>
            }
            <p style={{marginTop: "40px"}} className="acct-h1">Make Payment</p>
            <p className="acct-h2">Payment Amount £</p>
            <Input
                hint="Please enter payment amount."
                onBlur={e => setPaymentAmount(e.target.value)}/>
            {surcharge > 0 &&
                <p style={{fontWeight: 600, marginTop: 0, color: "var(--warning)", maxWidth: "600px"}}>
                    Total with surcharge: £{(Number(surcharge) + Number(paymentAmount)).toFixed(2)}
                </p>
            }
            <p className="acct-h2">Billing Address</p>
            {locations &&
                <AddressInput
                    selectedAddress={billingAddress}
                    locations={locations}
                    onSelect={setBillingAddress}
                    setCheckoutBlock={() => {
                    }}
                />
            }
            {!billingAddress &&
                <p style={{fontWeight: 600, margin: "20px 0", color: "var(--warning)"}}>
                    Please select a billing address
                </p>
            }
            <p className="acct-h2">Card Input</p>
            <LoadingSwitcher loading={loading}>
                <PaymentMethodSwitcher
                    paymentRules={[PaymentRules.K]}
                    selectedRule={PaymentRules.K}
                    onCardInput={setCardInput}
                    savedCards={savedCards}
                    onCardSelected={onSavedCardSelected}
                />
            </LoadingSwitcher>
            {hasChargeRate &&
                <p style={{fontWeight: 600, margin: "20px 0", color: "var(--warning)", maxWidth: "600px"}}>
                    {paymentAmount && paymentAmount > 0 ?
                        "Total with surcharge: £" + (Number(surcharge) + Number(paymentAmount)).toFixed(2) :
                        "This card has a surcharge, this will be added to the amount entered. Please enter a payment " +
                        "amount to see the total amount you will be charged."
                    }
                </p>
            }
            <Button
                style={{margin: "18px 0 100px 0"}}
                onClick={onPayClick}
                className={readyToMakePayment ? "payment-button" : "payment-button inactive"}>
                Make Payment
            </Button>
            {showPaymentMade &&
                <Popup
                    onOverlayClick={() => setShowPaymentMade(false)}
                >
                    <div className="page-payment-popup-container">
                        <p className="page-h2">Payment Made Successfully</p>
                        <p className="page-payment-notify">Your payment has been received. Thank You.</p>
                        <Button onClick={() => setShowPaymentMade(false)}>Back</Button>
                    </div>
                </Popup>
            }
            <PaymentProcessor session={session}/>
        </>
    )
}

export default MakePayment;
